// Audio file imports
import audioA from '../audio/a.mp3';
import audioASharp from '../audio/a_sharp.mp3';
import audioAFlat from '../audio/a_flat.mp3';
import audioB from '../audio/b.mp3';
import audioBFlat from '../audio/b_flat.mp3';
import audioC from '../audio/c.mp3';
import audioCSharp from '../audio/c_sharp.mp3';
import audioD from '../audio/d.mp3';
import audioDFlat from '../audio/d_flat.mp3';
import audioDSharp from '../audio/d_sharp.mp3';
import audioE from '../audio/e.mp3';
import audioEFlat from '../audio/e_flat.mp3';
import audioF from '../audio/f.mp3';
import audioFSharp from '../audio/f_sharp.mp3';
import audioG from '../audio/g.mp3';
import audioGFlat from '../audio/g_flat.mp3';
import audioGSharp from '../audio/g_sharp.mp3';
import audioCorrect from '../audio/correct.mp3';
import audioIncorrect from '../audio/incorrect.mp3';
import audioMajor from '../audio/major.mp3';
import audioMinor from '../audio/minor.mp3';

interface PreloadedAudio {
  audio: HTMLAudioElement;
  isLoaded: boolean;
  duration: number; // in milliseconds
}

class AudioPreloader {
  private audioMap: Map<string, PreloadedAudio> = new Map();
  private isPreloading = false;
  private preloadPromise: Promise<void> | null = null;

  private audioFiles = {
    // Note sounds
    'A': audioA,
    'A#': audioASharp,
    'Ab': audioAFlat,
    'B': audioB,
    'Bb': audioBFlat,
    'C': audioC,
    'C#': audioCSharp,
    'Db': audioDFlat,
    'D': audioD,
    'D#': audioDSharp,
    'Eb': audioEFlat,
    'E': audioE,
    'F': audioF,
    'F#': audioFSharp,
    'Gb': audioGFlat,
    'G': audioG,
    'G#': audioGSharp,
    
    // Feedback sounds
    'correct': audioCorrect,
    'incorrect': audioIncorrect,
    
    // Triad type sounds
    'major': audioMajor,
    'minor': audioMinor
  };

  async preloadAll(): Promise<void> {
    if (this.preloadPromise) {
      return this.preloadPromise;
    }

    this.isPreloading = true;
    
    this.preloadPromise = new Promise(async (resolve, reject) => {
      try {
        const loadPromises = Object.entries(this.audioFiles).map(([key, src]) => 
          this.preloadAudio(key, src)
        );
        
        await Promise.all(loadPromises);
        this.isPreloading = false;
        resolve();
      } catch (error) {
        this.isPreloading = false;
        reject(error);
      }
    });

    return this.preloadPromise;
  }

  private preloadAudio(key: string, src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.volume = 0.7; // Default volume
      
      const preloadedAudio: PreloadedAudio = {
        audio,
        isLoaded: false,
        duration: 0
      };

      this.audioMap.set(key, preloadedAudio);

      const onCanPlayThrough = () => {
        preloadedAudio.isLoaded = true;
        preloadedAudio.duration = audio.duration * 1000; // Convert to milliseconds
        audio.removeEventListener('canplaythrough', onCanPlayThrough);
        audio.removeEventListener('error', onError);
        resolve();
      };

      const onError = (error: Event) => {
        audio.removeEventListener('canplaythrough', onCanPlayThrough);
        audio.removeEventListener('error', onError);
        console.warn(`Failed to preload audio: ${key}`, error);
        // Don't reject - just mark as not loaded and continue
        resolve();
      };

      audio.addEventListener('canplaythrough', onCanPlayThrough);
      audio.addEventListener('error', onError);
      
      // Start loading
      audio.load();
    });
  }

  playAudio(key: string, volume: number = 0.7): boolean {
    const preloadedAudio = this.audioMap.get(key);
    
    if (!preloadedAudio || !preloadedAudio.isLoaded) {
      console.warn(`Audio not preloaded or failed to load: ${key}`);
      return false;
    }

    try {
      // Clone the audio to allow overlapping plays
      const audioClone = preloadedAudio.audio.cloneNode() as HTMLAudioElement;
      audioClone.volume = volume;
      audioClone.play().catch(error => {
        console.warn(`Failed to play audio: ${key}`, error);
      });
      return true;
    } catch (error) {
      console.warn(`Error playing audio: ${key}`, error);
      return false;
    }
  }

  isAudioLoaded(key: string): boolean {
    const preloadedAudio = this.audioMap.get(key);
    return preloadedAudio?.isLoaded || false;
  }

  getAudioDuration(key: string): number {
    const preloadedAudio = this.audioMap.get(key);
    return preloadedAudio?.duration || 0;
  }

  getLoadingProgress(): { loaded: number; total: number; percentage: number } {
    const total = Object.keys(this.audioFiles).length;
    const loaded = Array.from(this.audioMap.values()).filter(audio => audio.isLoaded).length;
    const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
    
    return { loaded, total, percentage };
  }

  isPreloadingComplete(): boolean {
    return !this.isPreloading && this.preloadPromise !== null;
  }
}

// Export singleton instance
export const audioPreloader = new AudioPreloader();
export default audioPreloader;